/**
 * episode-board-view.js -- THE EPISODE BOARD ON SCREEN (episodes.md 7.2).
 *
 * World > Episode > plot line > character. The void's `episode-board` frame
 * and the hand dealt for a route (`character_choice` carrying `cast`) render
 * as ONE view, mounted by two hosts: the shell's screen on the web path, and a
 * terminal panel when the shell is inactive (the fast path). Storyboard:
 * `frontend/web/preview/episode-board/`.
 *
 * WHAT IT NEVER SAYS. Every name a player reads arrives rendered from data: an
 * Episode's `episode_title`, a route's `title` (its plot line as its universe
 * presents it) and the hand's `cast.title` / `cast.heading`. This file names no
 * universe word, and a null title reads UNNAMED -- never an entity name.
 *
 * WHAT A CLICK SENDS. Exactly what typing sends: a route's number, a card's
 * number, or BACK. Choosing an Episode sends nothing; the frame already carries
 * every route of every Episode, and the view groups them.
 */

export const UNNAMED = 'unnamed';
export const BACK = 'back';

/** The board's routes grouped by Episode, in board order. Pure. */
export function boardModel(payload) {
    const episodes = [];
    const byKey = new Map();
    for (const r of payload.routes) {
        let ep = byKey.get(r.episode);
        if (!ep) {
            ep = { key: r.episode, title: r.episode_title, t: r.t, routes: [] };
            byKey.set(r.episode, ep);
            episodes.push(ep);
        }
        ep.routes.push(r);
    }
    for (const ep of episodes) ep.open = ep.routes.filter((r) => r.state === 'open').length;
    return { episodes, heading: payload.cast_heading };
}

/** A route's state, as the board states it. Pure. */
export function routeState(route) {
    return route.state === 'full'
        ? 'FULL -- everyone it lists is in play in this world'
        : `${route.free} can play`;
}

function esc(text) {
    return String(text)
        .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;').replace(/'/g, '&#39;');
}

function nameHtml(title) {
    return title === null || title === undefined
        ? `<span class="eb-unnamed">${UNNAMED}</span>`
        : esc(title);
}

/**
 * One board view in one host element. `send(text)` is the host's way of
 * answering the void -- the same call a typed line makes.
 */
export function createBoardView(doc, host, send) {
    const view = {
        board: null,      // the last board payload
        episode: null,    // the Episode the player opened, by key
        lastPick: null,   // the route number the player last chose

        /** Show a board. A re-offered board stays on the Episode it was opened at,
         *  and says so when the route just chosen is the one now full. */
        showBoard(payload) {
            const model = boardModel(payload);
            let notice = null;
            if (this.lastPick !== null) {
                const picked = payload.routes.find((r) => r.choice === this.lastPick);
                if (picked && picked.state === 'full') {
                    notice = picked.title === null
                        ? `That road is full: everyone it lists is in play in this world.`
                        : `${picked.title} is full: everyone it lists is in play in this world.`;
                }
                this.lastPick = null;
            }
            this.board = payload;
            if (!model.episodes.some((e) => e.key === this.episode)) this.episode = null;
            // One Episode is still shown as a choice: the clicks are the same in every world.
            this._render(this.episode === null ? episodesHtml(model) : routesHtml(model, this.episode), notice);
        },

        /** Show the hand dealt for a route: its title, its heading, its cards. */
        showHand(message) {
            this._render(handHtml(message), null);
        },

        clear() { host.innerHTML = ''; },

        _render(html, notice) {
            host.innerHTML = `<div class="eb-board">${notice ? `<div class="eb-notice">${esc(notice)}</div>` : ''}${html}</div>`;
            host.querySelectorAll('[data-episode]').forEach((el) => el.addEventListener('click', () => {
                this.episode = el.dataset.episode;
                this.showBoard(this.board);
            }));
            host.querySelectorAll('[data-episodes]').forEach((el) => el.addEventListener('click', () => {
                this.episode = null;
                this.showBoard(this.board);
            }));
            host.querySelectorAll('[data-route]').forEach((el) => el.addEventListener('click', () => {
                this.lastPick = Number(el.dataset.route);
                send(el.dataset.route);
            }));
            host.querySelectorAll('[data-card]').forEach((el) => el.addEventListener('click', () => {
                send(el.dataset.card);
            }));
            host.querySelectorAll('[data-back]').forEach((el) => el.addEventListener('click', () => {
                send(BACK);
            }));
        },
    };
    return view;
}

function episodesHtml(model) {
    const cards = model.episodes.map((ep) => {
        const t = ep.t === null ? '' : `T${ep.t} &middot; `;
        const roads = ep.routes.length === 1 ? '1 road' : `${ep.routes.length} roads`;
        return `
        <div class="eb-episode">
            <div class="eb-ep-name">${nameHtml(ep.title)}</div>
            <div class="eb-ep-sub">${t}${roads} &middot; ${ep.open} open</div>
            <button class="eb-cta" data-episode="${esc(ep.key)}">OPEN</button>
        </div>`;
    }).join('');
    return `<div class="eb-title">CHOOSE AN EPISODE</div>${cards}
        <div class="eb-hint">or type a road's number at the prompt</div>`;
}

function routesHtml(model, key) {
    const ep = model.episodes.find((e) => e.key === key);
    const rows = ep.routes.map((r) => {
        const full = r.state === 'full';
        return `
        <div class="eb-route${full ? ' full' : ''}">
            <span class="eb-num">${r.choice}</span>
            <span class="eb-route-name">${nameHtml(r.title)}</span>
            <span class="eb-state${full ? '' : ' open'}">${esc(routeState(r))}</span>
            ${full ? '<span class="eb-cta off">FULL</span>'
        : `<button class="eb-cta" data-route="${r.choice}">CHOOSE</button>`}
        </div>`;
    }).join('');
    return `<div class="eb-crumb" data-episodes>&larr; ${nameHtml(ep.title)}</div>
        <div class="eb-title">CHOOSE A ROAD</div>${rows}`;
}

function handHtml(message) {
    const cast = message.cast;
    const cards = (message.characters || []).map((c) => {
        const name = String(c.title || c.display_name || `Choice ${c.choice}`).replace(/\s{2,}/g, ' ').trim();
        const portrait = c.image_url
            ? `<img src="${esc(c.image_url)}" alt="${esc(name)}">`
            : '[ portrait ]';
        return `
        <div class="char-card eb-card">
            <div class="char-portrait">${portrait}</div>
            <div class="char-body">
                <div class="char-name">${esc(name)}</div>
                <div class="char-blurb">${esc(c.description || '')}</div>
                <button class="char-pick" data-card="${esc(String(c.choice))}">[${esc(String(c.choice))}] CHOOSE</button>
            </div>
        </div>`;
    }).join('');
    const heading = cast.heading ? `<div class="eb-cast-heading">${esc(cast.heading)}</div>` : '';
    return `<div class="eb-crumb" data-back>&larr; choose another road</div>
        <div class="eb-title">${nameHtml(cast.title)}</div>${heading}
        <div class="char-grid">${cards}</div>`;
}

const EpisodeBoardView = { UNNAMED, BACK, boardModel, routeState, createBoardView };

if (typeof window !== 'undefined') {
    window.EpisodeBoardView = EpisodeBoardView;
}

export default EpisodeBoardView;
